import { motion } from "framer-motion";
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Clock,
  Database,
  Flag,
  Link2,
  Loader2,
  Plug,
  ServerCog,
} from "lucide-react";
import { TopBar } from "@/components/layout/TopBar";
import { useSystemConfig, type IntegrationConfig, type FeatureFlag } from "@/hooks/useSystemConfig";
import { useSystemHealth, type HealthColor } from "@/hooks/useSystemHealth";
import { cn } from "@/lib/cn";

/**
 * System / Integrations — read-only page that shows what's configured,
 * what's syncing, what custom fields the planner auto-discovered, and
 * which feature flags are on. The goal is "you don't have to grep code
 * to know what the deploy is doing."
 *
 * No editing here yet — Phase 2 will make selected toggles editable.
 * Read-only is intentional for now: every toggle has a different
 * surface (env var vs. app_settings table vs. derived), and editing
 * each one safely is its own design conversation.
 */
export function System() {
  const config = useSystemConfig();
  const health = useSystemHealth();

  const isLoading = config.isLoading || health.isLoading;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TopBar
        title="System"
        subtitle="Integrations, scheduler, and feature flags — read-only"
      />
      <div className="flex-1 overflow-y-auto bg-canvas">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-ink-subtle">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading system info…
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="mx-auto max-w-5xl space-y-6 px-6 py-6"
          >
            {/* Overall status banner */}
            <OverallBanner
              status={(health.data?.status as HealthColor) ?? "unknown"}
              summary={health.data?.summary ?? ""}
            />

            {/* Integrations */}
            <Section
              icon={<Plug className="h-4 w-4 text-ink-muted" />}
              title="Integrations"
              subtitle="External systems the planner talks to"
            >
              <div className="space-y-3">
                {config.data?.integrations.map((int) => {
                  const healthRow = health.data?.integrations.find(
                    (h) => h.integration === int.name,
                  );
                  return (
                    <IntegrationCard
                      key={int.name}
                      integration={int}
                      healthStatus={(healthRow?.status as HealthColor) ?? "unknown"}
                      lastSync={healthRow?.last_success_at ?? null}
                      lastError={healthRow?.last_error_summary ?? null}
                    />
                  );
                })}
              </div>
            </Section>

            {/* Scheduler */}
            <Section
              icon={<Clock className="h-4 w-4 text-ink-muted" />}
              title="Scheduler"
              subtitle={
                config.data
                  ? `${config.data.scheduler.jobs.length} job${config.data.scheduler.jobs.length === 1 ? "" : "s"} · ${config.data.scheduler.timezone} · ${config.data.scheduler.weekdays_only ? "weekdays only" : "all days"}`
                  : ""
              }
            >
              {config.data && config.data.scheduler.jobs.length > 0 ? (
                <ul className="divide-y divide-line-soft rounded-lg border border-line bg-surface">
                  {config.data.scheduler.jobs.map((job) => (
                    <li
                      key={job.name}
                      className="flex items-center justify-between gap-3 px-4 py-3"
                    >
                      <div>
                        <div className="text-sm font-medium text-ink">
                          {job.name}
                        </div>
                        <div className="mt-0.5 text-[11px] text-ink-muted">
                          Integration: {job.integration}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-semibold text-ink-2 tabular-nums">
                          every {job.interval_minutes} min
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyHint message="No scheduled jobs are running. (Either nothing is configured, or all intervals are 0.)" />
              )}
            </Section>

            {/* Feature flags */}
            <Section
              icon={<Flag className="h-4 w-4 text-ink-muted" />}
              title="Feature flags"
              subtitle="What's on, what's off, and where each is controlled"
            >
              <div className="overflow-hidden rounded-lg border border-line bg-surface">
                <table className="w-full text-sm">
                  <thead className="border-b border-line-soft bg-canvas text-left text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
                    <tr>
                      <th className="px-4 py-2.5">Feature</th>
                      <th className="px-4 py-2.5">State</th>
                      <th className="px-4 py-2.5">Source</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line-soft">
                    {config.data?.feature_flags.map((flag) => (
                      <FlagRow key={flag.key} flag={flag} />
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            {/* Deploy info */}
            <Section
              icon={<Database className="h-4 w-4 text-ink-muted" />}
              title="Deploy"
              subtitle="Backend storage and API surface"
            >
              <div className="rounded-lg border border-line bg-surface p-4 text-sm">
                <KeyValueRow
                  label="Environment"
                  value={config.data?.deploy_env ?? "unknown"}
                />
                <KeyValueRow label="Database path" value={config.data?.db_path ?? ""} />
                <KeyValueRow label="API prefix" value={config.data?.api_prefix ?? ""} />
                <KeyValueRow
                  label="Daily backup"
                  value={
                    config.data?.backup_configured
                      ? "Configured (BACKUP_TOKEN set)"
                      : "Disabled — set BACKUP_TOKEN to enable"
                  }
                />
              </div>
            </Section>
          </motion.div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const STATUS_CHIP: Record<HealthColor, { label: string; cls: string }> = {
  green: { label: "Healthy", cls: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-200 dark:ring-emerald-500/30" },
  amber: { label: "Stale", cls: "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-500/30" },
  red: { label: "Failing", cls: "bg-rose-100 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-1 ring-rose-200 dark:ring-rose-500/30" },
  unknown: { label: "Unknown", cls: "bg-raised text-ink-muted ring-1 ring-line" },
};

function OverallBanner({ status, summary }: { status: HealthColor; summary: string }) {
  const chip = STATUS_CHIP[status];
  const Icon = status === "green" ? CheckCircle2 : status === "red" ? AlertCircle : Activity;
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl border bg-surface p-4 shadow-sm",
        status === "green" && "border-emerald-200 dark:border-emerald-500/30",
        status === "amber" && "border-amber-200 dark:border-amber-500/30",
        status === "red" && "border-rose-200 dark:border-rose-500/30",
        status === "unknown" && "border-line",
      )}
    >
      <Icon
        className={cn(
          "h-5 w-5 shrink-0",
          status === "green" && "text-emerald-600 dark:text-emerald-400",
          status === "amber" && "text-amber-600 dark:text-amber-400",
          status === "red" && "text-rose-600 dark:text-rose-400",
          status === "unknown" && "text-ink-subtle",
        )}
      />
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", chip.cls)}>
            {chip.label}
          </span>
          <span className="text-sm font-medium text-ink-2">{summary || "—"}</span>
        </div>
      </div>
    </div>
  );
}

function Section({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <header className="mb-2 flex items-center gap-2">
        {icon}
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
      </header>
      {subtitle && (
        <p className="mb-3 text-xs text-ink-muted">{subtitle}</p>
      )}
      {children}
    </section>
  );
}

function IntegrationCard({
  integration,
  healthStatus,
  lastSync,
  lastError,
}: {
  integration: IntegrationConfig;
  healthStatus: HealthColor;
  lastSync: string | null;
  lastError: string | null;
}) {
  const chip = STATUS_CHIP[healthStatus];
  const lastSyncLabel = lastSync
    ? new Date(lastSync).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "never";

  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="flex items-start justify-between gap-3 border-b border-line-soft px-4 py-3">
        <div className="flex items-center gap-3">
          <ServerCog className="h-4 w-4 text-ink-subtle" />
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold capitalize text-ink">
                {integration.name}
              </span>
              {integration.configured ? (
                <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider", chip.cls)}>
                  {chip.label}
                </span>
              ) : (
                <span className="rounded-full bg-raised px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
                  Not configured
                </span>
              )}
            </div>
            {integration.base_url && (
              <div className="mt-0.5 truncate text-[11px] text-ink-muted">
                {integration.base_url}
              </div>
            )}
          </div>
        </div>
        {integration.configured && (
          <div className="text-right text-[11px] text-ink-muted">
            <div>Last sync: {lastSyncLabel}</div>
            {integration.auto_sync_minutes != null && integration.auto_sync_minutes > 0 && (
              <div>Auto every {integration.auto_sync_minutes} min</div>
            )}
          </div>
        )}
      </div>

      {integration.configured && (
        <div className="space-y-3 px-4 py-3">
          {/* Config rows */}
          {integration.write_enabled !== null && integration.write_enabled !== undefined && (
            <KeyValueRow
              label="Write mode"
              value={integration.write_enabled ? "Enabled" : "Read-only"}
            />
          )}
          {integration.cache_ttl_minutes != null && (
            <KeyValueRow label="Cache TTL" value={`${integration.cache_ttl_minutes} min`} />
          )}
          {integration.project_keys && integration.project_keys.length > 0 && (
            <KeyValueRow label="Project keys" value={integration.project_keys.join(", ")} />
          )}

          {/* Fields pulled — translate customfield_NNNNN ids back to
              the human name we auto-discovered them under, so the chip
              row reads as "% Complete · Project Health · Dev Start
              Date" instead of opaque numeric ids. We humans need to
              know the names. Built-in field keys (summary, status,
              etc.) pass through unchanged. */}
          {integration.fields_pulled && integration.fields_pulled.length > 0 && (() => {
            // Reverse-index the discovered map (name → id) into id → name
            // so we can look up each chip by its raw id.
            const idToName: Record<string, string> = {};
            if (integration.discovered_fields) {
              for (const [name, id] of Object.entries(integration.discovered_fields)) {
                if (typeof id === "string" && id) idToName[id] = name;
              }
            }
            return (
              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
                  Fields synced
                </div>
                <div className="flex flex-wrap gap-1">
                  {integration.fields_pulled.map((f) => {
                    const human = idToName[f];
                    return (
                      <span
                        key={f}
                        title={human ? f : undefined}
                        className="rounded-md bg-raised px-2 py-0.5 text-[10px] text-ink-2"
                      >
                        {human ?? <span className="font-mono">{f}</span>}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Auto-discovered custom fields */}
          {integration.discovered_fields && Object.keys(integration.discovered_fields).length > 0 && (
            <div>
              <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
                <Link2 className="h-3 w-3" /> Auto-discovered custom fields
              </div>
              <div className="space-y-1">
                {Object.entries(integration.discovered_fields).map(([name, id]) => (
                  <div key={name} className="flex items-center justify-between text-[12px]">
                    <span className="text-ink-3">
                      {name}
                    </span>
                    <span className={cn(
                      "font-mono text-[10px]",
                      id ? "text-ink-2" : "text-ink-subtle italic",
                    )}>
                      {id || "not found"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Extra integration-specific fields */}
          {integration.extra && Object.entries(integration.extra).filter(([, v]) => v != null && v !== "").length > 0 && (
            <details className="text-[12px]">
              <summary className="cursor-pointer text-ink-muted hover:text-ink-2">
                More config
              </summary>
              <div className="mt-2 space-y-1 rounded-md bg-canvas p-2">
                {Object.entries(integration.extra)
                  .filter(([, v]) => v != null && v !== "")
                  .map(([k, v]) => (
                    <KeyValueRow key={k} label={k.replace(/_/g, " ")} value={String(v)} />
                  ))}
              </div>
            </details>
          )}

          {lastError && (
            <div className="rounded-md border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 px-3 py-2 text-[11px] text-rose-700 dark:text-rose-300">
              <span className="font-semibold">Last error: </span>
              {lastError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FlagRow({ flag }: { flag: FeatureFlag }) {
  return (
    <tr>
      <td className="px-4 py-2.5">
        <div className="text-sm text-ink">{flag.label}</div>
        {flag.notes && (
          <div className="mt-0.5 text-[11px] text-ink-muted">{flag.notes}</div>
        )}
      </td>
      <td className="px-4 py-2.5">
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-[11px] font-semibold",
            flag.enabled
              ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
              : "bg-raised text-ink-muted",
          )}
        >
          {flag.enabled ? "ON" : "OFF"}
        </span>
      </td>
      <td className="px-4 py-2.5">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
          {flag.source}
        </span>
      </td>
    </tr>
  );
}

function KeyValueRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-[12px]">
      <span className="text-ink-muted">{label}</span>
      <span className="truncate text-right text-ink tabular-nums">{value}</span>
    </div>
  );
}

function EmptyHint({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-line bg-surface px-4 py-6 text-center text-xs text-ink-muted">
      {message}
    </div>
  );
}

export default System;
